import { useState, useMemo, useCallback } from "react";
import { usePagination } from "../../../../hooks/usePagination";
import { useTenant } from "../../../../hooks/useTenant";
import { toast } from "react-toastify";
import { ConfirmDialog } from "../../../../components/Common";
import EventService from "../../../../services/event_service";

/**
 * useEvents
 * Hook de lógica para o módulo de Eventos.
 */
export const useEvents = () => {
    const { idTenant, user } = useTenant();
    const [isSaving, setIsSaving] = useState(false);

    const paginationOptions = useMemo(() => ({
        pageSize: 50,
        orderField: "createdAt",
        orderDirection: "desc"
    }), []);

    // Paginação dos Eventos (subcoleção tenants/{idTenant}/events)
    const { data: events, loading, error, refresh } = usePagination(
        `tenants/${idTenant}/events`,
        idTenant,
        paginationOptions
    );

    /**
     * Salvar um Evento
     */
    const saveEvent = useCallback(async (formData) => {
        if (!idTenant) return;
        setIsSaving(true);

        try {
            await EventService.save(formData, idTenant, user, events);
            toast.success(formData.id ? "Evento atualizado com sucesso!" : "Novo evento criado com sucesso!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao salvar evento:", err);
            toast.error(err?.message || "Erro ao salvar evento.");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, events, refresh]);

    /**
     * Excluir um Evento
     */
    const deleteEvent = useCallback(async (eventId, eventName) => {
        if (!idTenant || !user) return false;

        const confirmed = await ConfirmDialog({
            title: "Excluir Evento?",
            text: `Tem certeza que deseja excluir o evento "${eventName}"? Esta ação não pode ser desfeita.`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "danger"
        });

        if (!confirmed) return false;

        try {
            await EventService.delete(eventId, idTenant, user, eventName);
            toast.success("Evento excluído com sucesso!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao excluir evento:", err);
            toast.error(err?.message || "Não foi possível excluir o evento.");
            return false;
        }
    }, [idTenant, user, refresh]);

    return {
        events,
        loading,
        error,
        isSaving,
        actions: {
            saveEvent,
            deleteEvent,
            refresh
        }
    };
};
